import React, { useEffect, useState } from 'react';
import api from '../api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BackButton from '../components/BackButton';

export default function CashBookPage() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(''); // start blank
  const [ledger, setLedger] = useState([]);
  const [opening, setOpening] = useState(0);
  const [closing, setClosing] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [settings, setSettings] = useState({});
  const [search, setSearch] = useState('');

  // Fetch accounts and settings once on mount
  useEffect(() => {
    api.get('/cashbank-accounts').then(res => setAccounts(res.data));
    api.get('/settings/get').then(res => setSettings(res.data || {}));
  }, []);

  // Fetch ledger when selected account changes (if not blank)
  useEffect(() => {
    if (selected) {
      fetchLedger();
    } else {
      setLedger([]);
      setOpening(0);
      setClosing(0);
    }
  }, [selected]);

  // Fetch ledger data from backend
  function fetchLedger() {
    if (!selected) return; // guard against empty account

    const params = [];
    if (from) params.push(`from=${from}`);
    if (to) params.push(`to=${to}`);
    const paramStr = params.length ? `?${params.join('&')}` : '';

    api.get(`/cashbook/${selected}${paramStr}`)
      .then(res => {
        setLedger(res.data.ledger || []);
        setOpening(res.data.opening || 0);
        setClosing(res.data.closing || 0);
      })
      .catch(err => {
        console.error('Error fetching ledger:', err);
        setLedger([]);
        setOpening(0);
        setClosing(0);
      });
  }

  // Prepare company header info
  const phoneLine = [
    settings.Phone1 ? `Ph: ${settings.Phone1}` : '',
    settings.Phone2 ? `Ph: ${settings.Phone2}` : '',
    settings.Phone3 ? `Ph: ${settings.Phone3}` : ''
  ].filter(Boolean).join('   ');
  const companyLines = [
    settings.BusinessName || '',
    settings.Address || '',
    phoneLine,
    settings.GSTIN ? `GSTIN: ${settings.GSTIN}` : ''
  ].filter(Boolean);

  // Compute running balance rows including opening balance
  let running = Number(opening || 0);
  const allRows = [
    {
      Date: '',
      Particulars: 'Opening Balance',
      Type: '',
      InAmount: running,
      OutAmount: 0,
      Balance: running,
    },
    ...ledger.map(row => {
      running += (Number(row.InAmount) || 0) - (Number(row.OutAmount) || 0);
      return { ...row, Balance: running };
    })
  ];

  // Filter ledger rows by search
  const filteredRows = allRows.filter(row =>
    search.trim() === '' ||
    Object.values(row)
      .join(' ')
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  // Export to Excel handler
  const handleExportExcel = () => {
    const data = filteredRows.map(row => ({
      Date: row.Date ? new Date(row.Date).toLocaleDateString('en-GB') : '',
      Type: row.Type,
      Particulars: row.Particulars,
      'In (₹)': Number(row.InAmount || 0).toFixed(2),
      'Out (₹)': Number(row.OutAmount || 0).toFixed(2),
      Balance: Number(row.Balance || 0).toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CashBook');
    ws['A1'].v = (settings.BusinessName || '') + (settings.Address ? `, ${settings.Address}` : '');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${selected || 'Account'}_CashBook.xlsx`);
  };

  // Export to PDF handler
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(15);

    let pdfY = 16;
    companyLines.forEach(line => {
      doc.text(line, doc.internal.pageSize.getWidth() / 2, pdfY, { align: 'center' });
      pdfY += 7;
    });

    doc.setFontSize(12);
    doc.text(`${selected || 'Account'} Book`, doc.internal.pageSize.getWidth() / 2, pdfY, { align: 'center' });
    pdfY += 8;

    if (from || to) {
      doc.setFontSize(10);
      doc.text(`Period: ${from || '...'} to ${to || '...'}`, doc.internal.pageSize.getWidth() / 2, pdfY, { align: 'center' });
      pdfY += 6;
    }

    doc.setFontSize(11);
    doc.setFillColor(233, 245, 255);
    doc.roundedRect(14, pdfY - 3, 90, 12, 2, 2, 'F');
    doc.setTextColor(33, 37, 41);
    doc.text(`Opening Balance: ₹${Number(opening).toFixed(2)}`, 18, pdfY + 6);

    doc.setFillColor(231, 251, 233);
    doc.roundedRect(110, pdfY - 3, 90, 12, 2, 2, 'F');
    doc.setTextColor(25, 135, 84);
    doc.text(`Closing Balance: ₹${Number(closing).toFixed(2)}`, 114, pdfY + 6);

    doc.setTextColor(0, 0, 0);
    pdfY += 15;

    autoTable(doc, {
      startY: pdfY,
      head: [['Date', 'Type', 'Particulars', 'In (₹)', 'Out (₹)', 'Balance']],
      body: filteredRows.map(row => [
        row.Date ? new Date(row.Date).toLocaleDateString('en-GB') : '',
        row.Type,
        row.Particulars,
        Number(row.InAmount || 0).toFixed(2),
        Number(row.OutAmount || 0).toFixed(2),
        Number(row.Balance || 0).toFixed(2),
      ]),
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [0, 123, 255] },
      margin: { left: 14, right: 14 },
      theme: 'grid',
    });

    doc.save(`${selected || 'Account'}_CashBook.pdf`);
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 1100 }}>
      <BackButton />
      <div className="text-center mb-2">
        <h4 style={{ fontWeight: 'bold', marginBottom: 4 }}>{settings.BusinessName}</h4>
        <div style={{ fontSize: 15 }}>
          {settings.Address && <div>{settings.Address}</div>}
          {(settings.Phone1 || settings.Phone2 || settings.Phone3) && (
            <div>
              {[settings.Phone1, settings.Phone2, settings.Phone3]
                .filter(Boolean)
                .map((phone, i) => (
                  <span key={i}>Ph: {phone} &nbsp;&nbsp;</span>
                ))}
            </div>
          )}
          {settings.GSTIN && <div>GSTIN: {settings.GSTIN}</div>}
        </div>
      </div>
      <h2 className="text-center mb-4">{selected || 'Select Account'} Book</h2>

      <div className="row mb-3 justify-content-center">
        <div className="col-6 col-md-3">
          <div className="card shadow-sm border-info mb-2" style={{ background: '#e9f5ff' }}>
            <div className="card-body py-2 text-center">
              <div className="fw-bold" style={{ fontSize: 15 }}>Opening Balance</div>
              <div className="fs-5 text-info">₹{Number(opening).toFixed(2)}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card shadow-sm border-success mb-2" style={{ background: '#e7fbe9' }}>
            <div className="card-body py-2 text-center">
              <div className="fw-bold" style={{ fontSize: 15 }}>Closing Balance</div>
              <div className="fs-5 text-success">₹{Number(closing).toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-end mb-3 gap-2">
        <select
          className="form-select w-auto"
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="">-- Select Account --</option>
          {accounts.map(ac => (
            <option key={ac.AccountID} value={ac.AccountName}>
              {ac.AccountName}
            </option>
          ))}
        </select>
        <label className="ms-2">From:</label>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="form-control w-auto" />
        <label className="ms-2">To:</label>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} className="form-control w-auto" />
        <button className="btn btn-sm btn-primary ms-2" onClick={fetchLedger}>Apply</button>
        <input
          className="form-control w-auto ms-3"
          type="search"
          placeholder="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 160 }}
        />
        <div className="ms-auto">
          <button className="btn btn-outline-success me-2" onClick={handleExportExcel}>Export to Excel</button>
          <button className="btn btn-outline-primary" onClick={handleExportPDF}>Export to PDF</button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead className="table-info">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Particulars</th>
              <th>In (₹)</th>
              <th>Out (₹)</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-muted">No transactions</td>
              </tr>
            )}
            {filteredRows.map((row, i) => (
              <tr key={i}>
                <td>{row.Date ? new Date(row.Date).toLocaleDateString('en-GB') : ''}</td>
                <td>{row.Type}</td>
                <td>{row.Particulars}</td>
                <td>{Number(row.InAmount || 0).toFixed(2)}</td>
                <td>{Number(row.OutAmount || 0).toFixed(2)}</td>
                <td>{Number(row.Balance || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
